import React, { useEffect, useState, useMemo } from 'react';
import Modal from '@medflex/components/stories/Modal';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import { showAlertToast } from '@medflex/components/stories/Toast';
import SessionChatInvitationForm from '@medflex/components/stories/SessionChatInvitationForm';
import sortBy from 'lodash/sortBy';
import Logger from '@medflex/utils/Logger';
import isStaffUserId from '@medflex/utils/isStaffUserId';
import Button from '@medflex/components/stories/Button';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import useAddressBookContacts from '../../hooks/useAddressBookContacts';
import useChatContacts from '../../hooks/useChatContacts';
import useUserProfile from '../../hooks/useUserProfile';
import useModal from '../../hooks/useModal';
import useSessionChats from '../../hooks/useSessionChats';
import ContactsSelect from './ContactsSelect';

const SessionChatCreateModule = () => {
  useChatContacts();
  const { close } = useModal();
  const { userProfile } = useUserProfile();
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showSelector, setShowSelector] = useState(false);
  const { t } = useTranslation('messenger');
  const location = useLocation();
  const history = useHistory();
  const { staff, orgaPhysicians, pairedPatients, pairedPhysicians } = useAddressBookContacts();
  const { create: createSessionChat, chats: sessionChats } = useSessionChats();
  const [sessionChatState, setSessionChatState] = useState('idle');
  const groupChatContacts = useMemo(
    () =>
      [
        ...orgaPhysicians,
        ...staff.filter(member => member.pairedAt || !(!member.id || isStaffUserId(member.id))),
        ...pairedPatients,
        ...pairedPhysicians,
      ].filter(c => !c.mainContactId),
    [orgaPhysicians, pairedPatients, pairedPhysicians, staff],
  );

  const participants = useMemo(
    () =>
      selectedContacts.map(({ user }) => {
        const { id: userId, ...rest } = user;
        return { userId, ...rest };
      }),
    [selectedContacts],
  );

  const contacts = useMemo(
    () => sortBy([...groupChatContacts], ({ user }) => user.fullNameWithTitle),
    [groupChatContacts],
  );

  const handleClose = e => {
    e.preventDefault();
    close({ modal: 'sessionChatCreate' });
  };

  useEffect(() => {
    const { state } = location;
    if (state && state.selectedContact) {
      setSelectedContacts([state.selectedContact]);
    }
  }, [location]);

  const handleContactSelected = ({ contact }) => {
    if (selectedContacts.map(({ user }) => user.id).includes(contact.user.id)) {
      setSelectedContacts(selectedContacts.filter(({ user }) => user.id !== contact.user.id));
    } else if (selectedContacts.length < 50) {
      setSelectedContacts([...selectedContacts, contact]);
    }
  };

  const handleSessionChatSubmit = async data => {
    if (sessionChatState !== 'loading') {
      setSessionChatState('loading');
      try {
        const attributes = {
          expires: true,
          ...data,
          participants: [
            { userId: userProfile.id, roles: ['moderator'] },
            ...participants.map(p => ({ userId: p.userId })),
          ],
        };
        await createSessionChat(attributes);
        setSessionChatState('succeeded');
      } catch (e) {
        Logger.error(e);
        setSessionChatState('failed');
      }
    }
  };

  useEffect(() => {
    switch (sessionChatState) {
      case 'succeeded': {
        const session = sessionChats.slice(-1).shift();
        history.push(`/chat/${session.id}`);
        break;
      }
      case 'failed': {
        showAlertToast(t('SessionChatCreateModule.unexpectedError'), null, {
          toastId: 'sessionChatCreateFailed',
        });
        break;
      }
      default:
    }
  }, [sessionChatState, sessionChats]);

  return (
    <Modal
      title={t('components:SessionChatInvitationForm.title')}
      showCloseButton
      className="min-h-0 flex flex-col"
      onClickCloseButton={handleClose}
    >
      <SessionChatInvitationForm participants={participants} onSubmit={handleSessionChatSubmit}>
        {!showSelector && (
          <Button
            onClick={() => setShowSelector(true)}
            kind="link"
            className="items-center mt-4 space-x-2"
          >
            <PlusCircleIcon className="h-7 w-7" />
            <div>{t('SessionChatCreateModule.show_contacts_button')}</div>
          </Button>
        )}
        {showSelector && (
          <ContactsSelect
            contacts={contacts}
            selectedContacts={selectedContacts}
            onSelect={handleContactSelected}
          />
        )}
      </SessionChatInvitationForm>
    </Modal>
  );
};

export default SessionChatCreateModule;
